import { toDollarsShort } from '@/util/currency';
import { areShallowEqual } from '@/util/react';
import styleguide from '@common/styleguide';
import { AxisLeft } from '@visx/axis';
import { AxisProps } from '@visx/axis/lib/axis/Axis';
import { GridRows } from '@visx/grid';
import { memo } from 'react';
import { chartTheme, ScaleNumeric } from './chartHelper';

interface NumericYAxisProps extends AxisProps<ScaleNumeric> {
    scale: ScaleNumeric;
    formatter?: (value: number) => string;
    width: number;
    height: number;
    fontSize?: number;
}

export default memo(function NumericYAxisWithGrid({
    scale,
    formatter = toDollarsShort,
    width,
    height,
    fontSize = styleguide.typography.fontSizes.xs,
    ...axisProps
}: NumericYAxisProps) {
    const yDomain = scale.domain();
    const yDomainStart = yDomain[0];
    const yDomainEnd = yDomain[yDomain.length - 1];

    // Calculate the range and set an appropriate tick increment
    const range = yDomainEnd - yDomainStart;
    const increment = range / 2;

    // Determine mid tick value
    let yDomainMid = yDomainEnd - increment;

    // If domain includes both negative and positive values, check the proximity
    if (yDomainStart < 0 && yDomainEnd > 0) {
        // If the negative value is too close to zero, only show zero and positive ticks
        if (-yDomainStart < increment) {
            yDomainMid = yDomainEnd - increment * 2;
        } else if (yDomainEnd < increment) {
            // If the positive value is too close to zero, only show positive and negative ticks
            yDomainMid = yDomainStart - increment * 2;
        } else {
            yDomainMid = 0;
        }
    }

    // Ensure that the middle tick is not too close to the end ticks
    if (yDomainMid - yDomainStart < increment / 2) {
        yDomainMid = (yDomainStart + yDomainMid) / 2;
    }

    const tickValues = [yDomainEnd, yDomainMid, yDomainStart];

    return (
        <>
            <GridRows
                scale={scale}
                width={width}
                height={height}
                stroke={chartTheme.colors.gridline}
                tickValues={tickValues}
            />
            <AxisLeft
                scale={scale}
                hideAxisLine={true}
                hideTicks={true}
                tickValues={tickValues}
                tickFormat={(numberValue) =>
                    formatter(Math.round(numberValue.valueOf()))
                }
                tickLabelProps={() => ({
                    transform: 'translate(0 0)',
                    fontSize,
                    fill: chartTheme.colors.label,
                    textAnchor: 'start',
                })}
                left={-50}
                {...axisProps}
            />
        </>
    );
}, areShallowEqual);
